/**
 * @license Copyright (c) Microsoft Corporation.  All rights reserved.
 */
import { Logger } from "./Logger";
import { doesPhoneNumberStartWithCountryCode, findCheckboxes, getParentElement, findParentFieldBlock } from "./Utils/HtmlUtils";
import { LookupDataValueAttribute } from "./Lookups/LookupView";
import { LookupControlFactory } from "./Lookups/LookupControlFactory";
import { HtmlAttributes } from "../../../Common/Constants/HtmlAttributes";
import { WaitlistOptions } from "./EventManagement/Constants";
var FormSerializer = /** @class */ (function () {
    function FormSerializer() {
    }
    FormSerializer.serialize = function (form, builder) {
        if (!form) {
            this.logger.error("form was null");
            return null;
        }
        var elements = form.elements;
        for (var i = 0; i < elements.length; i++) {
            this.serializeElement(elements[i], builder);
        }
    };
    FormSerializer.serializeElement = function (currentElement, builder, overrides) {
        var _a;
        var name = (_a = overrides === null || overrides === void 0 ? void 0 : overrides.fieldName) !== null && _a !== void 0 ? _a : currentElement["name"];
        if (!name) {
            return;
        }
        if (this.isIgnoredField(currentElement)) {
            this.logger.log("ignored element ".concat(name));
            return;
        }
        switch (currentElement.nodeName) {
            case "INPUT":
                this.serializeInput(currentElement, name, builder, overrides);
                break;
            case "TEXTAREA":
                this.serializeTextArea(currentElement, name, builder, overrides);
                break;
            case "BUTTON":
                this.serializeButton(currentElement, name, builder, overrides);
                break;
            case "SELECT":
                this.serializeSelect(currentElement, name, builder, overrides);
                break;
            case "FIELDSET":
                this.serializeFieldSet(currentElement, name, builder, overrides);
                break;
            default:
                this.logger.log("ignored element ".concat(name, " with unknown type: ").concat(typeof currentElement));
                break;
        }
    };
    FormSerializer.resolveValue = function (rawValue, overrideValue) {
        var _a, _b;
        if (overrideValue === undefined) {
            return rawValue;
        }
        if (typeof overrideValue === "string") {
            return overrideValue;
        }
        return (_b = (_a = overrideValue.find(function (m) { return m.FormValue === rawValue; })) === null || _a === void 0 ? void 0 : _a.DataverseValue) !== null && _b !== void 0 ? _b : rawValue;
    };
    FormSerializer.updateLabels = function (builder, field, name, valueLabel, fieldLabel) {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        var block = findParentFieldBlock(field);
        if (!valueLabel && block) {
            try {
                var labels_1 = JSON.parse(block === null || block === void 0 ? void 0 : block.getAttribute((_a = HtmlAttributes.optionsValues) !== null && _a !== void 0 ? _a : "[]"));
                var input = field;
                var value_1 = input.value;
                if ((input === null || input === void 0 ? void 0 : input.type) === "checkbox" && labels_1.length > 1) {
                    value_1 = input.checked ? ((_c = (_b = labels_1[0]) === null || _b === void 0 ? void 0 : _b.value) !== null && _c !== void 0 ? _c : value_1) : ((_e = (_d = labels_1[1]) === null || _d === void 0 ? void 0 : _d.value) !== null && _e !== void 0 ? _e : "");
                }
                valueLabel = (_f = labels_1.filter(function (l) { return l.value == value_1; })[0]) === null || _f === void 0 ? void 0 : _f.label;
            }
            catch (_j) {
                // Ignore parse issue
            }
        }
        var labels = fieldLabel ? [fieldLabel] : Array.from((_h = (_g = field.labels) !== null && _g !== void 0 ? _g : (field.id && document.querySelectorAll("label[for='".concat(field.id, "']")))) !== null && _h !== void 0 ? _h : [], function (l) { return l.innerText; });
        builder.updateLabels(name, labels, valueLabel, block === null || block === void 0 ? void 0 : block.getAttribute(HtmlAttributes.savedFieldId));
    };
    FormSerializer.serializeInput = function (input, name, builder, overrides) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
        if (input.type === "file") {
            return;
        }
        var resolvedValue = this.resolveValue(input.value, overrides === null || overrides === void 0 ? void 0 : overrides.value);
        if (input.type === "datetime-local") {
            if (resolvedValue) {
                var parsedDate = Date.parse(resolvedValue.replace("T", " ").replace(/-/g, "/"));
                var utcDateTime = isNaN(parsedDate) ? resolvedValue : new Date(parsedDate).toISOString();
                builder.add(name, utcDateTime);
            }
            else {
                builder.add(name, resolvedValue);
            }
            this.updateLabels(builder, input, name);
            return;
        }
        if (input.type === "radio" || input.type === "checkbox") {
            if (input.type === "checkbox") {
                var parent = getParentElement(input, function (element) { return element.nodeName === "FIELDSET"; });
                if (parent && parent["name"] === input["name"]) {
                    // multi-select checkbox scenario
                    return;
                }
            }
            // Consent block
            if (input.type === "checkbox" &&
                (name.startsWith("msdynmkt_purposeid") || name.startsWith("msdynmkt_consenttypevalue") || name.startsWith("msdynmkt_topicid"))) {
                var labelText = overrides === null || overrides === void 0 ? void 0 : overrides.labelText;
                var complianceId = overrides === null || overrides === void 0 ? void 0 : overrides.complianceSettingsId;
                if (labelText === undefined) {
                    var labelElement = (_a = input.parentElement) === null || _a === void 0 ? void 0 : _a.querySelector("label[for=\"".concat(input.id, "\"]"));
                    labelText = (_c = (_b = labelElement === null || labelElement === void 0 ? void 0 : labelElement.textContent) === null || _b === void 0 ? void 0 : _b.trim()) !== null && _c !== void 0 ? _c : "";
                }
                if (complianceId === undefined) {
                    var query = name.startsWith("msdynmkt_topicid") ? '[data-editorblocktype="Topic"]' : '[data-editorblocktype="Consent"]';
                    complianceId = (_e = (_d = input.closest(query)) === null || _d === void 0 ? void 0 : _d.getAttribute("data-compliancesettingsid")) !== null && _e !== void 0 ? _e : "";
                }
                var values = resolvedValue.split(";");
                var purposeId = name.startsWith("msdynmkt_purposeid") ? values[0] : null;
                var topicId = name.startsWith("msdynmkt_topicid") ? values[0] : null;
                var consentTypeValue = name.startsWith("msdynmkt_consenttypevalue") ? values[0] : null;
                var jsonFieldName = "consentsubmissionvalues";
                var fieldValue = {
                    msdynmkt_purposeid: purposeId !== null && purposeId !== void 0 ? purposeId : null,
                    msdynmkt_topicid: topicId !== null && topicId !== void 0 ? topicId : null,
                    msdynmkt_consenttypevalue: consentTypeValue !== null && consentTypeValue !== void 0 ? consentTypeValue : null,
                    channels: (_f = values[1]) !== null && _f !== void 0 ? _f : null,
                    optinwhenchecked: (_g = values[2]) !== null && _g !== void 0 ? _g : null,
                    msdynmkt_value: input.checked ? "Opted In" : "Opted Out",
                    labelText: labelText,
                    msdynmkt_compliancesettings4id: complianceId
                };
                builder.add(jsonFieldName, JSON.stringify(fieldValue));
                this.updateLabels(builder, input, jsonFieldName, null, labelText);
                return;
            }
            // Event sessions block
            if (["checkbox", "radio"].includes(input.type) && name.startsWith("msevtmgt_session")) {
                var id = input.getAttribute("id");
                var sessionId = id.slice(0, id.indexOf("_"));
                var fieldKey = input.type === "radio" ? "".concat(name, ":").concat(sessionId) : name;
                if (input.checked) {
                    var sessionElement = input.closest("div.eventSession");
                    var capacity = sessionElement === null || sessionElement === void 0 ? void 0 : sessionElement.getAttribute(HtmlAttributes.dataAvailableSeats);
                    var waitlistStatus = sessionElement === null || sessionElement === void 0 ? void 0 : sessionElement.getAttribute(HtmlAttributes.dataWaitlistEnabled);
                    var isSessionWaitlisted = parseInt(capacity) <= 0 && waitlistStatus === WaitlistOptions.Yes;
                    var value = isSessionWaitlisted ? "waitlist" : resolvedValue;
                    builder.add(fieldKey, value);
                    var block = findParentFieldBlock(input);
                    this.updateLabels(builder, input, fieldKey, this.getLabel(input), (_h = block === null || block === void 0 ? void 0 : block.querySelector("h2")) === null || _h === void 0 ? void 0 : _h.innerText);
                }
                else {
                    builder.add(fieldKey, "");
                }
                return;
            }
            if (input.type === "radio") {
                var radioValue = builder.get(name);
                if (radioValue === undefined) {
                    builder.add(name, input.checked ? resolvedValue : "");
                    var block = findParentFieldBlock(input);
                    var label = Array.from((_j = block === null || block === void 0 ? void 0 : block.querySelectorAll("label")) !== null && _j !== void 0 ? _j : []).map(function (l) { return l.innerText; }).find(function (s) { return !!s; });
                    this.updateLabels(builder, input, name, this.getLabel(input), label);
                    return;
                }
                if (radioValue.value === "") {
                    builder.update(name, input.checked ? resolvedValue : "");
                    var block = findParentFieldBlock(input);
                    var label = Array.from((_k = block === null || block === void 0 ? void 0 : block.querySelectorAll("label")) !== null && _k !== void 0 ? _k : []).map(function (l) { return l.innerText; }).find(function (s) { return !!s; });
                    this.updateLabels(builder, input, name, this.getLabel(input), label);
                }
                return;
            }
            if (input.type === "checkbox") {
                input.checked ? builder.add(name, resolvedValue) : builder.add(name, "");
                this.updateLabels(builder, input, name);
            }
            return;
        }
        var lookupAttributeValue = input.getAttribute(LookupDataValueAttribute);
        if (lookupAttributeValue && lookupAttributeValue.length) {
            var parts = lookupAttributeValue.split(",");
            builder.add(name, parts[1], parts[0]);
            this.updateLabels(builder, input, name);
            return;
        }
        if (this.isLookupField(input)) {
            builder.add(name, "");
            this.updateLabels(builder, input, name);
            return;
        }
        if (input.type === "tel" && this.serializePhoneField(input, resolvedValue, name, builder)) {
            return;
        }
        builder.add(name, resolvedValue);
        this.updateLabels(builder, input, name);
    };
    FormSerializer.isLookupField = function (input) {
        return input.hasAttribute("list") || !!LookupControlFactory.getLookupFieldControlsFromInput(input);
    };
    FormSerializer.shouldAppendCountryCode = function (countryCode, inputValue) {
        return inputValue && countryCode && countryCode !== "+" && !doesPhoneNumberStartWithCountryCode(inputValue);
    };
    FormSerializer.serializeTextArea = function (input, name, builder, overrides) {
        builder.add(name, this.resolveValue(input.value, overrides === null || overrides === void 0 ? void 0 : overrides.value));
        this.updateLabels(builder, input, name);
    };
    FormSerializer.serializeButton = function (input, name, builder, overrides) {
        builder.add(name, this.resolveValue(input.value, overrides === null || overrides === void 0 ? void 0 : overrides.value));
    };
    FormSerializer.getLabel = function (input) {
        var _a, _b, _c, _d;
        var label = (_b = Array.from((_a = input.labels) !== null && _a !== void 0 ? _a : []).find(function (l) { return !!l.innerText; })) !== null && _b !== void 0 ? _b : Array.from((_d = (_c = input.parentElement) === null || _c === void 0 ? void 0 : _c.querySelectorAll("label")) !== null && _d !== void 0 ? _d : []).find(function (l) { return !!l.innerText; });
        return label === null || label === void 0 ? void 0 : label.innerText;
    };
    FormSerializer.serializeSelect = function (input, name, builder, overrides) {
        var _a, _b;
        if (input.type === "select-one") {
            var value = this.resolveValue(input.value, overrides === null || overrides === void 0 ? void 0 : overrides.value);
            builder.add(name, value);
            this.updateLabels(builder, input, name, (_b = (_a = input.selectedOptions) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.label);
        }
    };
    FormSerializer.serializeFieldSet = function (fieldSet, name, builder, overrides) {
        var _this = this;
        var checkboxes = findCheckboxes(fieldSet);
        var selected = Array.from(checkboxes !== null && checkboxes !== void 0 ? checkboxes : [])
            .filter(function (checkbox) { return checkbox.checked; });
        if (checkboxes === null || checkboxes === void 0 ? void 0 : checkboxes.length) {
            var checkboxesSerialized = JSON.stringify(selected.map(function (checkbox) { return _this.resolveValue(checkbox.value, overrides === null || overrides === void 0 ? void 0 : overrides.value); }));
            builder.add(name, checkboxesSerialized);
            this.updateLabels(builder, fieldSet, name, JSON.stringify(selected.map(function (c) {
                var _a;
                return ({
                    value: _this.resolveValue(c.value, overrides === null || overrides === void 0 ? void 0 : overrides.value),
                    label: c.labels && ((_a = c.labels[0]) === null || _a === void 0 ? void 0 : _a.innerText)
                });
            })));
        }
    };
    FormSerializer.isIgnoredField = function (element) {
        return element.getAttribute(this.ignoredFieldAttrName) === "true";
    };
    FormSerializer.serializePhoneField = function (input, value, name, builder) {
        var _a;
        var divBlockElement = (_a = input.parentElement) === null || _a === void 0 ? void 0 : _a.parentElement;
        var isInsidePhoneEditorBlock = divBlockElement &&
            divBlockElement.nodeType === Node.ELEMENT_NODE &&
            divBlockElement.nodeName.toLowerCase() === "div" &&
            divBlockElement.getAttribute(HtmlAttributes.blockType) === "PhoneFormField";
        var hasCountryCodePresent = isInsidePhoneEditorBlock && (divBlockElement === null || divBlockElement === void 0 ? void 0 : divBlockElement.hasAttribute(HtmlAttributes.dataCountryCode));
        if (hasCountryCodePresent) {
            var countryCode = divBlockElement.getAttribute(HtmlAttributes.dataCountryCode);
            builder.add(name, this.shouldAppendCountryCode(countryCode, value) ? countryCode + value : value);
            return true;
        }
        return false;
    };
    FormSerializer.ignoredFieldAttrName = "data-field-ignored";
    FormSerializer.logger = new Logger();
    return FormSerializer;
}());
export { FormSerializer };
